/**
 * ehr#636 related contact header. The drawer head shows the open chart's name with its
 * own relationship label beneath it, and each linked chart (ehr#208, Link charts on) to
 * the right with that person's label beneath their name.
 *
 * GET /contacts/:id/links gives one row per other chart:
 *   - 'out': this chart's Family & Contacts entry names them, so `relationship` says
 *     what THEY are to this client (Kadakia's chart: Chase = Parent);
 *   - 'in':  their chart names this one, so `relationship` says what THIS client is to
 *     them (Chase's chart, from Kadakia's entry: Chase = Parent, Kadakia = Child).
 * The labels are family roles (Parent, Child, Spouse, ...); the other side of a role
 * is its inverse (Parent <-> Child). A free-text "Other" relationship has no inverse,
 * so that side shows no label.
 */

export interface RelatedLinkInput {
  contactId: string;
  name: string;
  direction: 'out' | 'in';
  relationship: string;
}

export interface RelatedPerson {
  contactId: string;
  name: string;
  /** Their role relative to the open chart; '' when it cannot be named. */
  label: string;
}

export interface RelatedHeader {
  /** The open chart's own role; '' when no link names it. */
  selfLabel: string;
  related: RelatedPerson[];
}

/** Entered relationship (GUARDIAN_REL wording) -> header role label. */
const ROLE: Record<string, string> = {
  mother: 'Parent',
  father: 'Parent',
  parent: 'Parent',
  'step-mother': 'Step-parent',
  'step-father': 'Step-parent',
  'step-parent': 'Step-parent',
  'legal guardian': 'Legal guardian',
  grandparent: 'Grandparent',
  'grandparent (maternal)': 'Grandparent',
  'grandparent (paternal)': 'Grandparent',
  'aunt/uncle': 'Aunt/Uncle',
  'foster parent': 'Foster parent',
  'adoptive parent': 'Parent',
  husband: 'Spouse',
  wife: 'Spouse',
  spouse: 'Spouse',
  partner: 'Partner',
  child: 'Child',
};

/** Role label -> the role of the person on the other side. */
const INVERSE: Record<string, string> = {
  Parent: 'Child',
  'Step-parent': 'Step-child',
  'Legal guardian': 'Ward',
  Grandparent: 'Grandchild',
  'Aunt/Uncle': 'Niece/Nephew',
  'Foster parent': 'Foster child',
  Spouse: 'Spouse',
  Partner: 'Partner',
  Child: 'Parent',
};

/** The header label for an entered relationship (free text is kept as entered). */
export function roleLabel(relationship: string): string {
  const r = (relationship ?? '').trim();
  if (!r) return '';
  return ROLE[r.toLowerCase()] ?? r;
}

/** The other side's role, or '' when the relationship has no known inverse. */
export function inverseRole(relationship: string): string {
  return INVERSE[roleLabel(relationship)] ?? '';
}

const isCouple = (label: string) => label === 'Spouse' || label === 'Partner';

/**
 * The header for the open chart. Related people keep the API order. The open chart's
 * own label comes from the first link that names it, preferring a family role (Parent,
 * Child) over a couple role (Spouse, Partner): Chase with children and a wife = Parent.
 */
export function relatedHeader(links: RelatedLinkInput[]): RelatedHeader {
  const related: RelatedPerson[] = [];
  const selfLabels: string[] = [];
  for (const l of links) {
    const them = l.direction === 'out' ? roleLabel(l.relationship) : inverseRole(l.relationship);
    const me = l.direction === 'out' ? inverseRole(l.relationship) : roleLabel(l.relationship);
    related.push({ contactId: l.contactId, name: l.name, label: them });
    if (me) selfLabels.push(me);
  }
  const selfLabel = selfLabels.find((s) => !isCouple(s)) ?? selfLabels[0] ?? '';
  return { selfLabel, related };
}
